import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionType,
  type EntityComponent,
  type GameAction,
  type GameState,
  type SellWoolAction,
  type WoolProducerComponent,
} from '@sheepocalypse/shared';
import { useGameStore, useSocketStore, useLobbyStore } from '../../stores/socketStore';
import { Board } from '../board/Board';
import { PlayerList } from '../ui/PlayerList';
import { ENTITY_ASSETS, SHOP_TOOLTIP_BY_KEY } from '../ui/shopCatalog';

function isSellWoolAction(action: GameAction): action is SellWoolAction {
  return action.type === ActionType.SELL_WOOL;
}

function isWoolProducerComponent(component: EntityComponent): component is WoolProducerComponent {
  return component.type === 'WOOL_PRODUCER';
}

export function GameView() {
  const [floatingTooltip, setFloatingTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const {
    gameState,
    validActions,
    lastAction,
    placementFilter,
    setPlacementFilter,
    cardTargetingCardId,
    setCardTargetingCardId,
  } = useGameStore();
  const { requestValidActions, uiError, clearUiError, leaveGame, submitAction } = useSocketStore();
  const { playerId } = useLobbyStore();

  useEffect(() => {
    if (gameState && gameState.currentPlayerId === playerId) {
      requestValidActions();
    }
  }, [gameState?.updatedAt, gameState?.currentPlayerId, gameState?.turn, playerId]);

  if (!gameState) return null;

  const currentPlayer = gameState.players[gameState.currentPlayerId];
  const isSpectator = Boolean(playerId && !gameState.players[playerId]);
  const isMyTurn = gameState.currentPlayerId === playerId;
  const phase = getPhaseLabel(gameState, validActions, isMyTurn);
  const actionPrompt = getActionPrompt(gameState, validActions, isMyTurn, cardTargetingCardId);
  const specialEventLabel = getSpecialEventLabel(gameState.turnContext?.lastSpecial);
  const dieResult = gameState.turnContext?.lastRoll ?? 0;
  const winnerName = gameState.winner
    ? (gameState.players[gameState.winner]?.name ?? 'Unknown')
    : null;
  const eventHistory = useMemo(() => {
    return [...gameState.events].reverse().slice(0, 20);
  }, [gameState.events]);
  const popup = useTransientPopup(gameState, lastAction);
  const placeSheepActions = validActions.filter(a => a.type === ActionType.PLACE_SHEEP);
  const dachshundActions = validActions.filter(
    a => a.type === ActionType.PLACE_DOG && a.entityType === 'DACHSHUND'
  );
  const shepherdDogActions = validActions.filter(
    a => a.type === ActionType.PLACE_DOG && a.entityType === 'SHEPHERD_DOG'
  );
  const goldenDachshundActions = validActions.filter(
    a => a.type === ActionType.PLACE_DOG && a.entityType === 'GOLDEN_DACHSHUND'
  );
  const rollDiceAction = validActions.find(a => a.type === ActionType.ROLL_DICE);
  const sellWoolAction = validActions.find(isSellWoolAction);
  const buyCardAction = validActions.find(a => a.type === ActionType.BUY_CARD);
  const playCardActions = validActions.filter(a => a.type === ActionType.PLAY_CARD);
  const playCardActionsById = new Map<string, typeof playCardActions>();
  for (const action of playCardActions) {
    const existing = playCardActionsById.get(action.cardId) ?? [];
    playCardActionsById.set(action.cardId, [...existing, action]);
  }
  const endTurnAction = validActions.find(a => a.type === ActionType.END_TURN);
  const sellRemainingAmount = gameState.turnContext?.remainingSellAmount ?? 0;
  const sellRollCap = gameState.turnContext?.lastRoll ?? 0;
  const cardCost = gameState?.rules.cards.lambCardCostWool ?? 8;
  const myPlayer = playerId ? gameState.players[playerId] : null;
  const canAffordSheep =
    (myPlayer?.resources.GOLD ?? 0) >= (gameState.rules.entities.sheepCost ?? 0);
  const canAffordDachshund =
    (myPlayer?.resources.GOLD ?? 0) >= (gameState.rules.entities.dachshundCost ?? 0);
  const canAffordShepherd =
    (myPlayer?.resources.GOLD ?? 0) >= (gameState.rules.entities.shepherdDogCost ?? 0);
  const canAffordGolden =
    (myPlayer?.resources.GOLD ?? 0) >= (gameState.rules.entities.goldenDachshundUpgradeCost ?? 3);
  const canAffordCard = (myPlayer?.resources.WOOL ?? 0) >= cardCost;

  const handleRollDice = () => {
    if (rollDiceAction) {
      setPlacementFilter(null);
      setCardTargetingCardId(null);
      submitAction(rollDiceAction);
    }
  };

  const handleSellWool = () => {
    if (sellWoolAction) {
      setPlacementFilter(null);
      setCardTargetingCardId(null);
      submitAction(sellWoolAction);
    }
  };

  const handleEndTurn = () => {
    if (endTurnAction) {
      setPlacementFilter(null);
      setCardTargetingCardId(null);
      submitAction(endTurnAction);
    }
  };

  const handleBuyCard = () => {
    if (buyCardAction) {
      setPlacementFilter(null);
      setCardTargetingCardId(null);
      submitAction(buyCardAction);
    }
  };

  useEffect(() => {
    if (!cardTargetingCardId) {
      return;
    }

    const hasTargetingAction = playCardActions.some(
      action => action.cardId === cardTargetingCardId && action.targetEntityId
    );
    if (!hasTargetingAction) {
      setCardTargetingCardId(null);
    }
  }, [cardTargetingCardId, playCardActions, setCardTargetingCardId]);

  const phaseSteps = ['Income', 'Roll', 'Sell', 'Buy/Place', 'End'];

  const showTooltip = (text: string, x: number, y: number) => {
    setFloatingTooltip({ text, x, y });
  };

  const hideTooltip = () => {
    setFloatingTooltip(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e1f4ea] via-[#d9eff4] to-[#d2e8f4] p-4 xl:h-screen xl:overflow-hidden">
      <div className="mx-auto max-w-[1600px] xl:h-full">
        <div className="grid grid-cols-1 gap-4 xl:h-full xl:grid-cols-[300px_minmax(0,1fr)_300px]">
          <aside
            className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm xl:flex xl:h-full xl:flex-col"
            data-testid="action-panel"
          >
            <h1 className="text-3xl font-black tracking-tight text-slate-800">Sheepocalypse</h1>
            <p data-testid="turn-indicator" className="mt-2 text-base font-medium text-slate-600">
              Turn {gameState.turn}
            </p>
            <p data-testid="turn-status" className="mt-1 text-sm text-slate-700">
              {isSpectator ? (
                <span data-testid="spectator-mode-badge" className="text-indigo-700">
                  Spectator Mode
                </span>
              ) : isMyTurn ? (
                <span className="text-emerald-700">Your Turn!</span>
              ) : (
                <span>Waiting for {currentPlayer?.name}</span>
              )}
            </p>

            {isSpectator ? (
              <section
                className="mt-6 rounded-xl border border-slate-200 bg-white p-4"
                data-testid="spectator-panel"
              >
                <p className="text-sm text-slate-700">You are spectating an AI-only match.</p>
              </section>
            ) : (
              <>
                <section className="mt-4">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-xl border border-amber-200 bg-amber-100 px-3 py-3">
                      <p className="text-xs font-semibold text-amber-800">Gold</p>
                      <p data-testid="resource-gold" className="text-2xl font-black text-amber-900">
                        {myPlayer?.resources.GOLD ?? 0}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-xs font-semibold text-slate-700">Wool</p>
                      <p data-testid="resource-wool" className="text-2xl font-black text-slate-800">
                        {myPlayer?.resources.WOOL ?? 0}
                      </p>
                    </div>
                    {sellWoolAction ? (
                      <button
                        data-testid="sell-wool-button"
                        type="button"
                        onClick={handleSellWool}
                        className="rounded-xl border border-slate-300 bg-white px-2 py-2 text-center text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100"
                      >
                        Sell Wool
                        <span className="mt-1 block text-xs text-slate-500">
                          {sellRemainingAmount}/{sellRollCap}
                        </span>
                      </button>
                    ) : (
                      <button
                        data-testid="sell-wool-button"
                        type="button"
                        disabled
                        className="cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-2 py-2 text-center text-sm font-semibold text-slate-500"
                      >
                        Sell Wool
                        <span className="mt-1 block text-xs">
                          {sellRemainingAmount}/{sellRollCap}
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <p>
                      <span data-testid="resource-next-gold-income">
                        Next turn: +{calculateGoldenDachshundGoldIncome(gameState, playerId ?? '')}{' '}
                        gold
                      </span>
                    </p>
                    <p>
                      <span data-testid="resource-next-wool-income">
                        Next turn: +{calculateWoolIncome(gameState, playerId ?? '')} wool
                      </span>
                    </p>
                  </div>
                </section>

                <section
                  data-testid="buy-menu"
                  className="mt-5 rounded-xl border border-slate-200 bg-white p-3"
                >
                  <h3 className="text-xl font-bold text-slate-800">Buy Menu</h3>
                  <div className="mt-3 space-y-2">
                    <ShopItemButton
                      testId="shop-item-sheep"
                      icon={ENTITY_ASSETS.sheepBlue}
                      name="Sheep"
                      priceLabel={`${gameState.rules.entities.sheepCost}G`}
                      details={SHOP_TOOLTIP_BY_KEY.sheep}
                      isAffordable={canAffordSheep}
                      isEnabled={placeSheepActions.length > 0}
                      isSelected={placementFilter === 'PLACE_SHEEP'}
                      onClick={() => {
                        setCardTargetingCardId(null);
                        setPlacementFilter('PLACE_SHEEP');
                      }}
                      onTooltipVisible={showTooltip}
                      onTooltipHidden={hideTooltip}
                    />
                    <ShopItemButton
                      testId="shop-item-dachshund"
                      icon={ENTITY_ASSETS.dachshund}
                      name="Dachshund"
                      priceLabel={`${gameState.rules.entities.dachshundCost}G`}
                      details={SHOP_TOOLTIP_BY_KEY.dachshund}
                      isAffordable={canAffordDachshund}
                      isEnabled={dachshundActions.length > 0}
                      isSelected={placementFilter === 'DACHSHUND'}
                      onClick={() => {
                        setCardTargetingCardId(null);
                        setPlacementFilter('DACHSHUND');
                      }}
                      onTooltipVisible={showTooltip}
                      onTooltipHidden={hideTooltip}
                    />
                    <ShopItemButton
                      testId="shop-item-shepherd-dog"
                      icon={ENTITY_ASSETS.shepherdDog}
                      name="Shepherd Dog"
                      priceLabel={`${gameState.rules.entities.shepherdDogCost}G`}
                      details={SHOP_TOOLTIP_BY_KEY.shepherdDog}
                      isAffordable={canAffordShepherd}
                      isEnabled={shepherdDogActions.length > 0}
                      isSelected={placementFilter === 'SHEPHERD_DOG'}
                      onClick={() => {
                        setCardTargetingCardId(null);
                        setPlacementFilter('SHEPHERD_DOG');
                      }}
                      onTooltipVisible={showTooltip}
                      onTooltipHidden={hideTooltip}
                    />
                    <ShopItemButton
                      testId="shop-item-golden-dachshund"
                      icon={ENTITY_ASSETS.goldenDachshund}
                      name="Golden Dachshund"
                      priceLabel={`${gameState.rules.entities.goldenDachshundCost}G (${gameState.rules.entities.goldenDachshundUpgradeCost}G upgrade)`}
                      details={SHOP_TOOLTIP_BY_KEY.goldenDachshund}
                      isAffordable={canAffordGolden}
                      isEnabled={goldenDachshundActions.length > 0}
                      isSelected={placementFilter === 'GOLDEN_DACHSHUND'}
                      onClick={() => {
                        setCardTargetingCardId(null);
                        setPlacementFilter('GOLDEN_DACHSHUND');
                      }}
                      onTooltipVisible={showTooltip}
                      onTooltipHidden={hideTooltip}
                    />
                    <ShopItemButton
                      testId="shop-item-lamb-card"
                      icon={ENTITY_ASSETS.sheepYellow}
                      name="Lamb Card"
                      priceLabel={`${cardCost}W`}
                      details={SHOP_TOOLTIP_BY_KEY.lambCard}
                      isAffordable={canAffordCard}
                      isEnabled={Boolean(buyCardAction)}
                      isSelected={false}
                      onClick={handleBuyCard}
                      onTooltipVisible={showTooltip}
                      onTooltipHidden={hideTooltip}
                    />
                  </div>
                </section>

                <section
                  className="mt-5 rounded-xl border border-amber-200 bg-white p-3"
                  data-testid="hand-panel"
                >
                  <p className="text-xl font-bold text-slate-800">Your Lamb Cards</p>
                  {(myPlayer?.hand ?? []).length === 0 ? (
                    <p className="mt-2 text-xs text-slate-500">No cards in hand</p>
                  ) : (
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                      {myPlayer?.hand.map(cardId => {
                        const legalActionsForCard = playCardActionsById.get(cardId) ?? [];
                        const legalAction =
                          legalActionsForCard.find(action => !action.targetEntityId) ??
                          legalActionsForCard[0];
                        const requiresTargetSelection = legalActionsForCard.some(action =>
                          Boolean(action.targetEntityId)
                        );
                        return (
                          <button
                            key={cardId}
                            data-testid={`play-card-${cardId}`}
                            type="button"
                            disabled={!legalAction}
                            onClick={() => {
                              if (!legalAction) {
                                return;
                              }

                              setPlacementFilter(null);
                              if (requiresTargetSelection) {
                                setCardTargetingCardId(
                                  cardTargetingCardId === cardId ? null : cardId
                                );
                                return;
                              }

                              setCardTargetingCardId(null);
                              submitAction(legalAction);
                            }}
                            onMouseEnter={event => {
                              const info = getCardPopupInfo(cardId);
                              showTooltip(
                                `${info.title}: ${info.description}`,
                                event.clientX,
                                event.clientY
                              );
                            }}
                            onMouseMove={event => {
                              const info = getCardPopupInfo(cardId);
                              showTooltip(
                                `${info.title}: ${info.description}`,
                                event.clientX,
                                event.clientY
                              );
                            }}
                            onMouseLeave={hideTooltip}
                            onFocus={event => {
                              const rect = event.currentTarget.getBoundingClientRect();
                              const info = getCardPopupInfo(cardId);
                              showTooltip(
                                `${info.title}: ${info.description}`,
                                rect.right + 8,
                                rect.top + rect.height / 2
                              );
                            }}
                            onBlur={hideTooltip}
                            className={`min-w-16 rounded border px-2 py-2 text-left text-xs transition-colors ${
                              legalAction
                                ? cardTargetingCardId === cardId
                                  ? 'border-blue-400 bg-blue-50 text-blue-900 ring-1 ring-blue-300'
                                  : 'border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-800'
                                : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                            title={cardId}
                          >
                            <img
                              className="h-7 w-full object-contain"
                              src="/assets/cards/card_back.png"
                              alt="Lamb Card"
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>

                {buyCardAction && (
                  <button
                    data-testid="buy-card-button"
                    onClick={handleBuyCard}
                    className="mt-4 w-full rounded-lg border border-orange-200 bg-orange-100 py-2 text-sm font-semibold text-orange-800 transition-colors hover:bg-orange-200"
                  >
                    Buy Lamb Card (-{cardCost} wool)
                  </button>
                )}
              </>
            )}

            {gameState.phase !== 'ENDED' && (
              <button
                data-testid="leave-game-button"
                type="button"
                onClick={leaveGame}
                className="mt-6 rounded-lg border border-slate-300 bg-white px-4 py-2 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100 xl:mt-auto"
              >
                Leave Game
              </button>
            )}
          </aside>

          <main className="space-y-3 xl:flex xl:h-full xl:min-h-0 xl:flex-col">
            <section
              className="rounded-2xl border border-cyan-100 bg-white/90 p-3 shadow-sm"
              data-testid="phase-panel-right"
            >
              <div className="grid grid-cols-[130px_minmax(0,1fr)_130px] items-start gap-4 flex items-center align-center">
                <div className="flex items-start justify-start">
                  <div className="flex flex-col items-center gap-2">
                    <DiceResult value={dieResult > 0 ? dieResult : 1} />
                    {rollDiceAction && (
                      <button
                        data-testid="roll-dice-button"
                        type="button"
                        onClick={handleRollDice}
                        className="rounded-full bg-blue-700 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-800"
                      >
                        roll dice
                      </button>
                    )}
                  </div>
                </div>

                <div className="min-w-0 text-center">
                  <p
                    data-testid="phase-current"
                    className="mt-1 text-center text-sm font-semibold text-blue-700"
                  >
                    {phase}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                    {phaseSteps.map(step => (
                      <span
                        key={step}
                        className={`rounded-full px-3 py-1 text-sm font-semibold ${
                          step === phase
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {step}
                      </span>
                    ))}
                  </div>
                  <p data-testid="action-prompt" className="mt-2 text-sm text-slate-700">
                    {actionPrompt}
                  </p>
                  {gameState.phase === 'SETUP' && (
                    <p className="mt-1 text-sm text-amber-800">Setup: place your King Sheep</p>
                  )}
                  {gameState.turnContext?.lastSpecial === 'BLACK_SHEEP_MOVE' && (
                    <p className="mt-1 text-sm text-slate-700">
                      Roll 1: move or place the Black Sheep now
                    </p>
                  )}
                  {specialEventLabel && (
                    <p data-testid="special-event-banner" className="mt-1 text-sm text-rose-700">
                      Special event: {specialEventLabel}
                    </p>
                  )}
                </div>

                <button
                  data-testid="end-turn-button"
                  type="button"
                  disabled={!isMyTurn || !endTurnAction}
                  onClick={handleEndTurn}
                  className={`w-[130px] rounded-xl px-3 py-2 text-base font-medium transition-colors ${
                    isMyTurn && endTurnAction
                      ? 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                      : 'cursor-not-allowed bg-slate-100 text-slate-400'
                  }`}
                >
                  End Turn
                </button>
              </div>
              {uiError && (
                <div className="mt-3 flex items-center justify-between rounded border border-red-200 bg-red-50 p-2">
                  <p data-testid="action-error" className="text-sm text-red-700">
                    {uiError}
                  </p>
                  <button
                    type="button"
                    onClick={clearUiError}
                    className="text-xs text-red-700 underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </section>

            {gameState.phase === 'ENDED' && (
              <section className="rounded-xl bg-white p-4 shadow" data-testid="game-over-panel">
                <h2 className="text-xl font-bold text-gray-800">Game Over</h2>
                <p data-testid="game-over-winner" className="mt-1 text-gray-700">
                  Winner: {winnerName}
                </p>
                <button
                  data-testid="leave-game-button"
                  type="button"
                  onClick={leaveGame}
                  className="mt-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-800 hover:bg-gray-200"
                >
                  Leave Game
                </button>
              </section>
            )}

            {popup && (
              <div
                data-testid="event-popup"
                className={`fixed left-1/2 top-6 z-50 -translate-x-1/2 transition-all duration-300 ${
                  popup.isClosing ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              >
                <div
                  className={`flex min-w-[280px] items-center gap-3 rounded-xl border px-4 py-3 shadow-lg ${
                    popup.variant === 'card'
                      ? 'border-amber-300 bg-amber-100 text-amber-900'
                      : 'border-rose-300 bg-rose-100 text-rose-900'
                  }`}
                >
                  {popup.variant === 'card' && (
                    <img
                      src="/assets/cards/card_back.png"
                      alt="Lamb Card"
                      className="h-8 w-8 object-contain"
                    />
                  )}
                  <div>
                    <p className="text-sm font-semibold">{popup.title}</p>
                    <p className="text-xs">{popup.description}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="xl:min-h-0 xl:flex-1">
              <Board />
            </div>
          </main>

          <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm xl:h-full xl:overflow-hidden">
            <PlayerList />
            <section
              className="mt-6 rounded-xl border border-slate-200 bg-white p-4"
              data-testid="event-history-panel"
            >
              <h3 className="text-xl font-bold text-slate-800">Recent Actions</h3>
              <div
                className="mt-2 max-h-48 space-y-1 overflow-y-auto"
                data-testid="event-history-list"
              >
                {eventHistory.length === 0 ? (
                  <p className="text-sm text-slate-500">No actions yet</p>
                ) : (
                  eventHistory.map(event => (
                    <p key={event.id} className="text-sm text-slate-700">
                      {toReadableEventLine(gameState, event.playerId, event.type)}
                    </p>
                  ))
                )}
              </div>
            </section>
          </aside>
        </div>

        {floatingTooltip && (
          <div
            data-testid="shop-item-tooltip"
            role="tooltip"
            className="pointer-events-none fixed z-50 max-w-[280px] rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow-lg"
            style={{ left: floatingTooltip.x + 14, top: floatingTooltip.y + 14 }}
          >
            {floatingTooltip.text}
          </div>
        )}
      </div>
    </div>
  );
}

function ShopItemButton(props: {
  testId: string;
  icon: string;
  name: string;
  priceLabel: string;
  details: string;
  isAffordable: boolean;
  isEnabled: boolean;
  isSelected: boolean;
  onClick: () => void;
  onTooltipVisible: (tooltip: string, x: number, y: number) => void;
  onTooltipHidden: () => void;
}) {
  const tooltip = `${props.details} Cost: ${props.priceLabel}.`;

  return (
    <div
      data-testid={`${props.testId}-tooltip-target`}
      tabIndex={0}
      onMouseEnter={event => props.onTooltipVisible(tooltip, event.clientX, event.clientY)}
      onMouseMove={event => props.onTooltipVisible(tooltip, event.clientX, event.clientY)}
      onMouseLeave={props.onTooltipHidden}
      onFocus={event => {
        const rect = event.currentTarget.getBoundingClientRect();
        props.onTooltipVisible(tooltip, rect.right + 8, rect.top + rect.height / 2);
      }}
      onBlur={props.onTooltipHidden}
    >
      <button
        data-testid={props.testId}
        type="button"
        disabled={!props.isEnabled}
        onClick={props.onClick}
        className={`w-full rounded-md border px-2 py-2 text-left transition-colors ${
          props.isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
        } ${props.isEnabled ? 'hover:bg-slate-50' : 'cursor-not-allowed opacity-75'} ${
          props.isAffordable ? 'text-slate-800' : 'text-slate-400'
        }`}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <img className="h-6 w-6 object-contain" src={props.icon} alt={props.name} />
            <span className="text-base leading-none">{props.name}</span>
          </span>
          <span className="text-sm font-semibold">{props.priceLabel}</span>
        </span>
      </button>
    </div>
  );
}

type PopupState = {
  id: string;
  title: string;
  description: string;
  variant: 'special' | 'card';
  isClosing: boolean;
};

function useTransientPopup(gameState: any, lastAction: any): PopupState | null {
  const [popup, setPopup] = useState<PopupState | null>(null);
  const lastSpecialKeyRef = useRef<string>('');
  const lastCardActionIdRef = useRef<string>('');

  useEffect(() => {
    const special = gameState.turnContext?.lastSpecial;
    if (!special) {
      return;
    }

    const title = getSpecialEventLabel(special);
    if (!title) {
      return;
    }

    const id = `special-${special}-${gameState.updatedAt}`;
    if (lastSpecialKeyRef.current === `${special}:${gameState.turn}`) {
      return;
    }

    lastSpecialKeyRef.current = `${special}:${gameState.turn}`;
    setPopup({
      id,
      title,
      description: `Special event triggered: ${title}.`,
      variant: 'special',
      isClosing: false,
    });
  }, [gameState.turnContext?.lastSpecial, gameState.updatedAt]);

  useEffect(() => {
    if (!lastAction || lastAction.type !== ActionType.PLAY_CARD || !lastAction.cardId) {
      return;
    }

    if (lastCardActionIdRef.current === lastAction.id) {
      return;
    }

    lastCardActionIdRef.current = lastAction.id;

    const info = getCardPopupInfo(lastAction.cardId);
    const id = `card-${lastAction.id}`;
    setPopup({
      id,
      title: info.title,
      description: info.description,
      variant: 'card',
      isClosing: false,
    });
  }, [lastAction?.id]);

  useEffect(() => {
    if (!popup) {
      return;
    }

    const startClose = setTimeout(() => {
      setPopup(current =>
        current && current.id === popup.id ? { ...current, isClosing: true } : current
      );
    }, 1200);

    const clearPopup = setTimeout(() => {
      setPopup(current => (current && current.id === popup.id ? null : current));
    }, 1500);

    return () => {
      clearTimeout(startClose);
      clearTimeout(clearPopup);
    };
  }, [popup]);

  return popup;
}

function getCardPopupInfo(cardId: string): { title: string; description: string } {
  const cardType = cardId.split('-')[0];
  const descriptions: Record<string, string> = {
    DOUBLE_PRICE: 'Doubles wool sell value for this turn.',
    MOVE_BLACK_SHEEP: 'Move or place the Black Sheep immediately.',
    PAINT_BUCKET: 'Select one of your sheep to recolor adjacent enemy sheep.',
    WOLF_IN_SHEEPS_CLOTHING: 'Select one opponent sheep to remove from the board.',
    WOLF_PACK: 'Triggers Wolf Pack removals by protection rules.',
  };

  return {
    title: cardType,
    description: descriptions[cardType] ?? 'Lamb Card activated.',
  };
}

function calculateGoldenDachshundGoldIncome(gameState: GameState, playerId: string): number {
  const player = gameState.players[playerId];
  if (!player) {
    return 0;
  }

  let income = 0;

  const getTileByPosition = (x: number, y: number) => gameState.board.tiles[`${x},${y}`];

  const getNeighbors = (x: number, y: number): Array<{ x: number; y: number }> =>
    [
      { x: x - 1, y },
      { x: x + 1, y },
      { x, y: y - 1 },
      { x, y: y + 1 },
    ].filter(position => Boolean(getTileByPosition(position.x, position.y)));

  const goldenDachshunds = Object.values(gameState.entities).filter(
    entity => entity?.type === 'GOLDEN_DACHSHUND'
  );

  for (const goldenDachshund of goldenDachshunds) {
    for (const neighbor of getNeighbors(goldenDachshund.position.x, goldenDachshund.position.y)) {
      const tile = getTileByPosition(neighbor.x, neighbor.y);
      if (!tile) {
        continue;
      }

      for (const entityId of tile.entities) {
        const entity = gameState.entities[entityId];
        if (
          (entity?.type === 'SHEEP' || entity?.type === 'KING_SHEEP') &&
          player.entities.includes(entityId)
        ) {
          income += 1;
        }
      }
    }
  }

  return income;
}

function calculateWoolIncome(gameState: GameState, playerId: string): number {
  const player = gameState.players[playerId];
  if (!player) {
    return 0;
  }

  let income = 0;

  for (const entityId of player.entities) {
    const entity = gameState.entities[entityId];
    if (entity?.type === 'SHEEP' || entity?.type === 'KING_SHEEP') {
      const producer = entity.components.find(isWoolProducerComponent);
      if (producer) {
        income += producer.rate;
      }
    }
  }

  return income;
}

function DiceResult({ value }: { value: number }) {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    if (!value || value < 1 || value > 6) {
      return;
    }

    let ticks = 0;
    const animation = setInterval(() => {
      ticks += 1;
      setDisplayValue(Math.floor(Math.random() * 6) + 1);
      if (ticks >= 8) {
        clearInterval(animation);
        setDisplayValue(value);
      }
    }, 75);

    return () => clearInterval(animation);
  }, [value]);

  const pips = getPips(displayValue);

  return (
    <div className="mt-2" data-testid="die-result">
      <svg
        data-testid="die-svg"
        aria-label={`Die result ${value}`}
        viewBox="0 0 100 100"
        className="w-12 h-12"
      >
        <rect x="5" y="5" width="90" height="90" rx="12" fill="#f3f4f6" stroke="#9ca3af" />
        {pips.map(([cx, cy], index) => (
          <circle key={index} cx={cx} cy={cy} r="7" fill="#1f2937" />
        ))}
      </svg>
    </div>
  );
}

function getPips(value: number): Array<[number, number]> {
  const layout: Record<number, Array<[number, number]>> = {
    1: [[50, 50]],
    2: [
      [30, 30],
      [70, 70],
    ],
    3: [
      [30, 30],
      [50, 50],
      [70, 70],
    ],
    4: [
      [30, 30],
      [70, 30],
      [30, 70],
      [70, 70],
    ],
    5: [
      [30, 30],
      [70, 30],
      [50, 50],
      [30, 70],
      [70, 70],
    ],
    6: [
      [30, 25],
      [70, 25],
      [30, 50],
      [70, 50],
      [30, 75],
      [70, 75],
    ],
  };

  return layout[value] ?? layout[1];
}

function toReadableEventLine(gameState: any, playerId: string, actionType: ActionType): string {
  const playerName = gameState.players[playerId]?.name ?? 'Unknown player';
  const phraseByAction: Record<string, string> = {
    [ActionType.PLACE_KING_SHEEP]: 'placed a King Sheep',
    [ActionType.PLACE_SHEEP]: 'placed a sheep',
    [ActionType.PLACE_DOG]: 'placed a dog',
    [ActionType.ROLL_DICE]: 'rolled the die',
    [ActionType.SELL_WOOL]: 'sold wool',
    [ActionType.BUY_CARD]: 'bought a Lamb Card',
    [ActionType.PLAY_CARD]: 'played a Lamb Card',
    [ActionType.MOVE_BLACK_SHEEP]: 'moved the Black Sheep',
    [ActionType.END_TURN]: 'ended their turn',
  };

  return `${playerName} ${phraseByAction[actionType] ?? 'performed an action'}`;
}

function getSpecialEventLabel(lastSpecial?: string): string | null {
  if (lastSpecial === 'WOLF_PACK') {
    return 'Wolf Pack';
  }

  if (lastSpecial === 'EXPLOSION') {
    return 'Black Sheep Explosion';
  }

  if (lastSpecial === 'BLACK_SHEEP_MOVE') {
    return 'Move Black Sheep';
  }

  return null;
}

function getPhaseLabel(gameState: any, validActions: any[], isMyTurn: boolean): string {
  if (gameState.phase === 'SETUP') {
    return 'Setup';
  }

  if (gameState.turnContext?.fieldFullResolution?.isActive) {
    return 'Field Full';
  }

  if (!isMyTurn) {
    return 'Income';
  }

  if (gameState.turnContext?.lastSpecial === 'BLACK_SHEEP_MOVE') {
    return 'Roll';
  }

  if ((gameState.turnContext?.lastRoll ?? 0) === 0) {
    return 'Roll';
  }

  if (validActions.some(action => action.type === ActionType.SELL_WOOL)) {
    return 'Sell';
  }

  if (validActions.some(action => action.type === ActionType.END_TURN)) {
    return 'Buy/Place';
  }

  return 'End';
}

function getActionPrompt(
  gameState: any,
  validActions: any[],
  isMyTurn: boolean,
  cardTargetingCardId: string | null
): string {
  if (isMyTurn && cardTargetingCardId) {
    return 'Select a highlighted sheep on the board to resolve this Lamb Card.';
  }

  if (gameState.turnContext?.fieldFullResolution?.isActive) {
    if (!isMyTurn) {
      return `Field is full: waiting for ${gameState.players[gameState.currentPlayerId]?.name} to optionally activate one Lamb Card.`;
    }

    return 'Field is full: optionally activate one Lamb Card, then end your turn.';
  }

  if (!isMyTurn) {
    return `Waiting for ${gameState.players[gameState.currentPlayerId]?.name} to act.`;
  }

  if (gameState.phase === 'SETUP') {
    return 'Place your King Sheep on a highlighted tile to finish setup.';
  }

  if (gameState.turnContext?.lastSpecial === 'BLACK_SHEEP_MOVE') {
    return 'Roll 1 triggered: choose a highlighted tile to place or move the Black Sheep.';
  }

  if ((gameState.turnContext?.lastRoll ?? 0) === 0) {
    return 'Start your turn by rolling the die.';
  }

  if (validActions.some(action => action.type === ActionType.SELL_WOOL)) {
    return 'Sell wool now, then continue with purchases or placements.';
  }

  if (validActions.some(action => action.type === ActionType.PLACE_SHEEP)) {
    return 'Place an entity on a highlighted tile or end your turn.';
  }

  return 'End your turn when you are done.';
}
